import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { signOut } from '../auth/useSession';
import { COLORS } from '../theme';

// Desktop-first shell. Left sidebar navigation + wide content column.
// No customer-facing links anywhere — the surface is intentionally opaque.
const NAV: Array<{ to: string; label: string }> = [
  { to: '/',           label: 'Mission Control' },
  { to: '/dashboard',  label: 'Dashboard' },
  { to: '/operations', label: 'Operations' },
  { to: '/alerts',     label: 'Alerts' },
  { to: '/analyst',    label: 'Analyst' },
];

export function Layout({ email }: { email: string | null }) {
  return (
    <div style={rootStyle}>
      <Sidebar email={email} />
      <main style={mainStyle}>
        <Outlet />
      </main>
    </div>
  );
}

function Sidebar({ email }: { email: string | null }) {
  return (
    <aside style={sidebarStyle}>
      <div style={brandRow}>
        <span style={brandKicker}>REVLOOM</span>
        <span style={brandStamp}>ops</span>
      </div>
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {NAV.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.to === '/'} style={({ isActive }) => ({
            ...navLink,
            background: isActive ? COLORS.surfaceAlt : 'transparent',
            color:      isActive ? COLORS.text : COLORS.textSecondary,
          })}>{item.label}</NavLink>
        ))}
      </nav>
      <div style={{ flex: 1 }} />
      {email ? (
        <div style={sidebarFooter}>
          <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 6 }}>Signed in</div>
          <div style={{ color: COLORS.textSecondary, fontSize: 12, marginBottom: 12, wordBreak: 'break-all' }}>{email}</div>
          <button onClick={() => { void signOut(); }} style={signOutBtn}>Sign out</button>
        </div>
      ) : null}
    </aside>
  );
}

export function PageHeader({ title, right }: { title: string; right?: ReactNode }) {
  return (
    <div style={pageHeaderStyle}>
      <h1 style={pageTitleStyle}>{title}</h1>
      {right}
    </div>
  );
}

const rootStyle: React.CSSProperties = {
  display: 'flex', minHeight: '100vh', background: COLORS.bg, color: COLORS.text,
};
const sidebarStyle: React.CSSProperties = {
  width: 240, background: COLORS.surface, borderRight: `1px solid ${COLORS.border}`,
  padding: '24px 16px', display: 'flex', flexDirection: 'column',
  position: 'sticky', top: 0, height: '100vh',
};
const mainStyle: React.CSSProperties = {
  flex: 1, padding: '32px 40px', maxWidth: 1240, width: '100%',
};
const brandRow: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 32, padding: '0 8px' };
const brandKicker: React.CSSProperties = { letterSpacing: 3, fontSize: 11, color: COLORS.textMuted, fontWeight: 600 };
const brandStamp: React.CSSProperties = {
  fontSize: 10, background: COLORS.accent, color: '#0A0B0D',
  padding: '2px 6px', borderRadius: 4, fontWeight: 700,
};
const navLink: React.CSSProperties = {
  padding: '10px 12px', borderRadius: 8, textDecoration: 'none',
  fontSize: 13, fontWeight: 500,
};
const sidebarFooter: React.CSSProperties = {
  borderTop: `1px solid ${COLORS.border}`, paddingTop: 16,
};
const signOutBtn: React.CSSProperties = {
  width: '100%', background: 'transparent', color: COLORS.textSecondary,
  border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '8px',
  fontSize: 12, cursor: 'pointer',
};
const pageHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 24,
};
const pageTitleStyle: React.CSSProperties = {
  fontSize: 26, fontWeight: 600, margin: 0, letterSpacing: -0.3,
};
